/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { db, auth, storage } from "../firebase";
import {
    collection,
    getDocs,
    doc,
    getDoc,
    query,
    where,
    addDoc,
    updateDoc,
    setDoc,
    serverTimestamp,
    runTransaction,
    orderBy,
    limit,
    writeBatch,
    increment,
    arrayUnion
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { getISTDate } from "../utils/dateUtils";
import { getOrInitStock, calculateClosing } from "../utils/stockUtils";
import BlankLayout from "../components/BlankLayout";
import { generateOrderInvoicePdf } from "../utils/pdfOrderInvoice";
import { generateStockStatusPdf } from "../utils/pdfStockStatus";
import { printToBluetoothDevice } from "../utils/bluetoothPrinter";
import { FaHistory, FaBoxes, FaStore, FaSignOutAlt, FaEdit, FaMobileAlt, FaDirections, FaMapMarkerAlt, FaWhatsapp } from "react-icons/fa";

const SKU_MASTER_COL = "skuMaster";

const getIstDateStr = getISTDate;

function DsMobileDashboard() {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // Workflow State: "beat" -> "outlet" -> "order" -> "preview" -> "success"
    const [step, setStep] = useState("beat");

    const [selectedBeat, setSelectedBeat] = useState("");
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [rawOutlets, setRawOutlets] = useState([]); // Base data from Firestore
    const [lastOrderMap, setLastOrderMap] = useState({});
    const [skuMaster, setSkuMaster] = useState({});
    const [skuList, setSkuList] = useState([]);
    const [orderItems, setOrderItems] = useState({});

    const [location, setLocation] = useState(null);
    const [locLoading, setLocLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // New Outlet Form State
    const [newOutletForm, setNewOutletForm] = useState({ name: "", phone: "" });
    const [capturedLat, setCapturedLat] = useState(null);
    const [capturedLong, setCapturedLong] = useState(null);
    const [capLoading, setCapLoading] = useState(false);
    const [outletPhoto, setOutletPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0); 
    const [uploadStatus, setUploadStatus] = useState("");
    const [beatCounts, setBeatCounts] = useState({});

    const [beatMap, setBeatMap] = useState({}); 
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [beatCache, setBeatCache] = useState({}); // Cache for outlets and order maps per beat
    const [companyInfo, setCompanyInfo] = useState({});
    const [lastOrder, setLastOrder] = useState(null);

    // History & Inventory State
    const [historyOrders, setHistoryOrders] = useState([]);
    const [historyDate, setHistoryDate] = useState(getIstDateStr());
    const [showHistory, setShowHistory] = useState(false);
    const [selectedHistoryOrder, setSelectedHistoryOrder] = useState(null);
    const [showStock, setShowStock] = useState(false);
    const [todayStats, setTodayStats] = useState({ count: 0, qty: 0 });
    const [ordersCountForAutoSort, setOrdersCountForAutoSort] = useState(0);

    const [editingOutlet, setEditingOutlet] = useState(null);

    const [inventoryDate, setInventoryDate] = useState(getIstDateStr());
    const [inventoryRows, setInventoryRows] = useState([]);
    const [invLoading, setInvLoading] = useState(false);

    const [sortByDistance, setSortByDistance] = useState(false);
    
    // Stability: Prevent redundant loads
    const [isStockLoading, setIsStockLoading] = useState(false);
    
    const stockLevels = useMemo(() => {
        const map = {};
        inventoryRows.forEach(r => {
            map[r.sku] = r.closing;
        });
        return map;
    }, [inventoryRows]);

    const renderHeader = (title, backTo) => (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, width: "100%", maxWidth: "500px", margin: "0 auto", padding: "16px 24px", background: "rgba(255, 255, 255, 0.85)", backdropFilter: "blur(24px)", borderBottom: "1px solid rgba(0,0,0,0.05)", zIndex: 1100, display: "flex", alignItems: "center", gap: 16 }}>
            <button 
                onClick={() => !submitting && setStep(backTo)} 
                disabled={submitting}
                style={{ border: "none", background: "none", fontSize: 24, padding: 0, color: submitting ? "#ccc" : "var(--text-main)", cursor: submitting ? "not-allowed" : "pointer" }}
            >←</button>
            <h1 style={{ fontSize: 20, fontWeight: 900, margin: 0 }}>{title}</h1>
        </div>
    );
    const getDistance = (lat1, lon1, lat2, lon2) => {
        if (!lat1 || !lon1 || !lat2 || !lon2) return 999999;
        const R = 6371; // km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };


    const minHistoryDate = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7); 
        return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    }, []);

    const maxHistoryDate = getIstDateStr();

    const currencySymbol = useMemo(() => {
        if (!companyInfo?.country) return "₹";
        if (companyInfo.country.toLowerCase() === "us") return "$";
        return "₹";
    }, [companyInfo]);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    const snap = await getDoc(doc(db, "users", user.uid));
                    if (snap.exists()) {
                        const profile = snap.data();
                        if (profile.status !== "active") {
                            alert("Your account is inactive. Please contact your administrator.");
                            auth.signOut();
                            return;
                        }
                        setUserProfile(profile);
                    }
                }
            } catch (err) {
                console.warn("Profile fetch failed (likely offline):", err);
            } finally {
                setLoading(false);
            }
        });
        return () => unsub();
    }, [navigate]);

    // Load Beat Names (Areas) from DS profile
    // Load Beat Names + Distributor Info from DS Master
    useEffect(() => {
        if (!userProfile?.dsId) return;
        const loadDsInfo = async () => {
            try {
                const dsSnap = await getDoc(doc(db, "dsMaster", userProfile.dsId));
                if (dsSnap.exists()) {
                    const data = dsSnap.data();
                    // Set Beams/Areas
                    setBeatMap({
                        "MON": data.monArea || "",
                        "TUE": data.tueArea || "",
                        "WED": data.wedArea || "",
                        "THU": data.thuArea || "",
                        "FRI": data.friArea || "",
                        "SAT": data.satArea || ""
                    });
                    // Set Distributor Info (Simple lookup from DS Master)
                    setCompanyInfo({
                        partyId: data.distributorId || data.partyId || "",
                        name: data.distributorName || data.partyName || ""
                    });
                }
            } catch (err) {
                console.error("Error loading DS/Distributor info:", err);
            }
        };
        loadDsInfo();
    }, [userProfile]);

    // Load Stock Status Details - HIGHLY OPTIMIZED (1 Document Read)
    const loadStockStatus = async (dateStr) => {
        if (!userProfile?.dsId || isStockLoading) return;
        setIsStockLoading(true);
        setInvLoading(true);
        try {
            // Use the centralized robust carry-forward utility
            const summaryDoc = await getOrInitStock("ds", userProfile.dsId, dateStr, userProfile.clientId);
            const inv = summaryDoc?.inventory || {};
            
            const rows = skuList.map(sku => {
                const skuUpper = sku.trim().toUpperCase();
                const m = skuMaster[skuUpper] || {};
                const info = inv[skuUpper] || { opening: 0 };
                
                const opening = Number(info.opening || 0);
                const billed = Number(info.billed || 0) + Number(info.received || 0);
                const sold = Number(info.sold || 0) + Number(info.sale || 0);
                const closing = opening + billed - sold;
                
                return {
                    sku: skuUpper,
                    name: m.Name || skuUpper,
                    opening,
                    billed,
                    sold,
                    closing
                };
            }).filter(r => r.opening !== 0 || r.billed > 0 || r.sold > 0);

            setInventoryRows(rows);

            if (dateStr === maxHistoryDate) {
                const totalSold = rows.reduce((sum, r) => sum + r.sold, 0);
                const shopCount = summaryDoc?.billedOutlets?.length || 0;
                setTodayStats({ count: shopCount, qty: totalSold });
            }
        } catch (err) {
            console.error("Error loading stock status:", err);
        } finally {
            setInvLoading(false);
            setIsStockLoading(false);
        }
    };

    useEffect(() => {
        if (userProfile?.dsId) {
            loadStockStatus(inventoryDate);
        }
    }, [inventoryDate, userProfile?.dsId, skuList, refreshTrigger]); // Only refresh on data-changing events

    // Load SKUs
    useEffect(() => {
        if (!userProfile) return;
        const loadSkus = async () => {
            let q = query(
                collection(db, SKU_MASTER_COL),
                where("clientId", "==", userProfile.clientId)
            );
            let snap = await getDocs(q);
            if (snap.empty) {
                q = query(collection(db, SKU_MASTER_COL));
                snap = await getDocs(q);
            }
            const map = {};
            const list = [];
            snap.forEach((d) => {
                const data = d.data();
                const identifier = String(data.sku || data.Name || "").trim().toUpperCase();
                map[identifier] = data;
                list.push(identifier);
            });
            setSkuMaster(map);
            setSkuList(list);
        };
        loadSkus();
    }, [userProfile]);
    
    // Fetch Outlet Counts per Beat for the Dashboard
    useEffect(() => {
        if (!userProfile?.dsId) return;
        const fetchBeatCounts = async () => {
            try {
                const q = query(
                    collection(db, "outletMaster"),
                    where("clientId", "==", userProfile.clientId),
                    where("dsId", "==", userProfile.dsId),
                    where("status", "==", "active")
                );
                const snap = await getDocs(q);
                const counts = {};
                snap.forEach(d => {
                    const b = d.data().beat;
                    if (b) counts[b] = (counts[b] || 0) + 1;
                });
                setBeatCounts(counts);
            } catch (err) {
                console.error("Error fetching beat counts:", err);
            }
        };
        fetchBeatCounts();
    }, [userProfile?.dsId, userProfile?.clientId, refreshTrigger]);

    // 1. Data Fetching Effect: Only runs when Beat changes
    useEffect(() => {
        if (!userProfile || !selectedBeat) return;
        const loadOutletsData = async () => {
            // Check cache first (Session-based optimization)
            if (beatCache[selectedBeat]) {
                setRawOutlets(beatCache[selectedBeat].outlets);
                setLastOrderMap(beatCache[selectedBeat].orderMap);
                return;
            }

            try {
                // Fetch Outlets
                const qOutlets = query(
                    collection(db, "outletMaster"),
                    where("clientId", "==", userProfile.clientId),
                    where("beat", "==", selectedBeat),
                    where("status", "==", "active"),
                    where("dsId", "==", userProfile.dsId || "")
                );
                const outletSnap = await getDocs(qOutlets);
                const fetchedOutlets = outletSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                // Fetch Recent Orders to understand billing pattern and last visit (limited to last 50 for cost efficiency)
                const qOrders = query(
                    collection(db, "Outlet_Orders"),
                    where("clientId", "==", userProfile.clientId),
                    where("dsId", "==", userProfile.dsId),
                    where("beat", "==", selectedBeat),
                    orderBy("createdAt", "desc"),
                    limit(50)
                );
                const ordersSnap = await getDocs(qOrders);
                const orderInfoMap = {};
                
                ordersSnap.docs.forEach(d => {
                    const data = d.data();
                    const oid = data.outletId;
                    const date = data.createdAt?.toDate();
                    if (date) {
                        if (!orderInfoMap[oid] || date > orderInfoMap[oid].date) {
                            orderInfoMap[oid] = {
                                date: date,
                                amount: data.totalAmount || 0,
                                timeInSeconds: date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds()
                            };
                        }
                    }
                });

                // Update state and cache
                setRawOutlets(fetchedOutlets);
                setLastOrderMap(orderInfoMap);
                setBeatCache(prev => ({
                    ...prev,
                    [selectedBeat]: { outlets: fetchedOutlets, orderMap: orderInfoMap }
                }));
            } catch (err) {
                console.error("Error loading outlets/orders:", err);
            }
        };
        loadOutletsData();
    }, [selectedBeat, userProfile?.dsId, userProfile?.clientId, refreshTrigger]);

    // 2. Local Logic: Sorting and Distance Calculation (Zero Cost - In-Memory)
    const outlets = useMemo(() => {
        let processed = [...rawOutlets];
        
        // Always calculate distances if location is available
        if (location) {
            processed = processed.map(o => ({
                ...o,
                dist: getDistance(location.lat, location.lng, o.lat, o.long)
            }));
        }

        if (sortByDistance && location) {
            processed.sort((a, b) => (a.dist || 999999) - (b.dist || 999999));
        } else {
            processed.sort((a, b) => {
                const timeA = lastOrderMap[a.outletId]?.timeInSeconds || 999999;
                const timeB = lastOrderMap[b.outletId]?.timeInSeconds || 999999;
                return timeA - timeB;
            });
        }
        return processed;
    }, [rawOutlets, lastOrderMap, sortByDistance, location]);

    const handleBeatSelect = (beat) => {
        setSelectedBeat(String(beat));
        setStep("outlet");
    };

    const handleOutletSelect = (outlet) => {
        setSelectedOutlet(outlet);
        setStep("order");
        captureLocation();
    };

    const captureLocation = () => {
        if (!navigator.geolocation) return;
        setLocLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setLocLoading(false);
            },
            () => setLocLoading(false),
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
    };

    const onChangeQty = (sku, val) => {
        let nVal = val === "" ? "" : Number(val);
        if (isNaN(nVal)) nVal = 0;
        nVal = Math.max(0, nVal);
        setOrderItems({ ...orderItems, [sku]: nVal });
    };

    const totals = useMemo(() => {
        let totalQty = 0;
        let totalAmount = 0;
        let totalBasic = 0;
        let totalTax = 0;

        Object.entries(orderItems).forEach(([sku, qty]) => {
            const q = Number(qty || 0);
            if (q <= 0) return;
            totalQty += q;
            const m = skuMaster[sku];
            if (m) {
                const basicEway = Number(m.Basic_Eway) || 0;
                const gstP = Number(m.gst) || 0;
                const mrp = Number(m.mrp) || 0;
                const skuType = (m.skutype || "").toLowerCase();

                let lineBasic = q * basicEway;
                let lineTax = 0;
                if (skuType === "tobacco") {
                    const unitBasicRate = mrp / ((100 + gstP) / 100);
                    lineTax = q * (mrp - unitBasicRate);
                } else {
                    lineTax = lineBasic * (gstP / 100);
                }
                totalAmount += (lineBasic + lineTax);
                totalBasic += lineBasic;
                totalTax += lineTax;
            }
        });
        return { totalQty, totalAmount, totalBasic, cgst: totalTax / 2, sgst: totalTax / 2 };
    }, [orderItems, skuMaster]);

    const handleConfirmOrder = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const cleanItems = {};
            Object.entries(orderItems).forEach(([sku, qty]) => {
                if (Number(qty) > 0) cleanItems[sku] = Number(qty);
            });
            const todayStr = getIstDateStr();
            const itemLines = Object.entries(cleanItems).map(([sku, qty]) => {
                const skuKey = String(sku).trim().toUpperCase();
                const m = skuMaster[skuKey] || {};
                const basicRate = Number(m.Basic_Eway) || 0;
                const gstP = Number(m.gst) || 0;
                const mrp = Number(m.mrp) || 0;
                const skuType = (m.skutype || "").toLowerCase();
                let lineBasic = qty * basicRate;
                let lineTax = 0;
                if (skuType === "tobacco") {
                    const unitBasicRate = mrp / ((100 + gstP) / 100);
                    lineTax = qty * (mrp - unitBasicRate);
                } else {
                    lineTax = lineBasic * (gstP / 100);
                }
                return {
                    sku: skuKey,
                    skuName: m.Name || skuKey,
                    qty: qty,
                    basicRate: basicRate,
                    basicAmount: lineBasic,
                    cgstRate: gstP / 2,
                    sgstRate: gstP / 2,
                    cgstAmount: lineTax / 2,
                    sgstAmount: lineTax / 2,
                    totalAmount: lineBasic + lineTax,
                    hsn: m.hsn || "",
                    skuType: skuType,
                    mrp: mrp,
                    orderDate: todayStr
                };
            });

            const payload = {
                clientId: userProfile.clientId,
                distributorId: companyInfo.partyId || "",
                distributorName: companyInfo.name || "",
                dsId: userProfile.dsId || "UNKNOWN",
                outletId: selectedOutlet.outletId,
                outletName: selectedOutlet.name,
                outletPhone: selectedOutlet.phone || "",
                beat: selectedBeat,
                items: cleanItems,
                itemLines: itemLines,
                totalQty: totals.totalQty,
                totalAmount: totals.totalAmount,
                totalBasic: totals.totalBasic,
                cgst: totals.cgst,
                sgst: totals.sgst,
                lat: location?.lat || capturedLat || null,
                long: location?.lng || capturedLong || null,
                status: "APPROVED",
                source: "DS_MOBILE",
                approvedAt: serverTimestamp(),
                clientTimestamp: new Date().toISOString(), // Local fallback
                approvedDateOnly: todayStr,
                createdAt: serverTimestamp(),
            };

            const saveOrderOffline = async () => {
                const todayStr = getIstDateStr();
                const stockDocId = `${payload.dsId}_${todayStr}`;
                const stockRef = doc(db, "ds_stock_daily", stockDocId);
                
                const tempId = Date.now().toString().slice(-6);
                payload.invoiceNo = `OFF-${tempId}`;
                payload.status = "PENDING_SYNC";

                // Atomic local updates
                setDoc(stockRef, { dsId: payload.dsId, date: todayStr, clientId: payload.clientId, initialized: true }, { merge: true });
                const invUpdates = {
                    updatedAt: serverTimestamp(),
                    billedOutlets: arrayUnion(payload.outletId),
                    totalBills: increment(1)
                };
                Object.entries(cleanItems).forEach(([sku, qty]) => {
                    invUpdates[`inventory.${sku}.sold`] = increment(qty);
                });
                updateDoc(stockRef, invUpdates);
            };

            // Instant-On Strategy: Don't let spotty network hang the UI
            if (!navigator.onLine) {
                await saveOrderOffline();
            } else {
                try {
                    // Try server transaction but TIMEOUT after 1.5 seconds
                    await Promise.race([
                        runTransaction(db, async (transaction) => {
                            const distId = payload.distributorId || "GEN";
                            const counterRef = doc(db, "counters", `invoice_${distId}`);
                            const stockRef = doc(db, "ds_stock_daily", `${payload.dsId}_${getIstDateStr()}`);

                            const counterSnap = await transaction.get(counterRef);
                            let seq = (counterSnap.exists() ? (counterSnap.data().lastSeq || 0) : 0) + 1;
                            
                            const initials = (payload.distributorName || "QB").split(" ").map(w => w[0]).join("").substring(0, 2).toUpperCase();
                            const year = new Date().getFullYear().toString().substring(2);
                            payload.invoiceNo = `${initials}${year}${String(seq).padStart(5, '0')}`;

                            transaction.set(counterRef, { lastSeq: seq }, { merge: true });
                            const stockUpdates = {
                                billedOutlets: arrayUnion(payload.outletId),
                                totalBills: increment(1),
                                updatedAt: serverTimestamp()
                            };
                            Object.entries(cleanItems).forEach(([sku, qty]) => {
                                stockUpdates[`inventory.${sku}.sold`] = increment(qty);
                            });
                            transaction.set(stockRef, { dsId: payload.dsId, date: getIstDateStr(), clientId: payload.clientId, initialized: true }, { merge: true });
                            transaction.update(stockRef, stockUpdates);
                        }),
                        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1500))
                    ]);
                } catch (tErr) {
                    console.warn("Server handshake slow/failed. Switching to Local Vault.", tErr);
                    await saveOrderOffline();
                }
            }

            // NON-BLOCKING SAVE: Send to outbox and proceed immediately
            addDoc(collection(db, "Outlet_Orders"), payload).catch(err => console.warn("Background sync queued:", err));
            
            setOrdersCountForAutoSort(prev => prev + 1);
            setLastOrder(payload);
            setRefreshTrigger(prev => prev + 1);
            setStep("success");
            if (navigator.vibrate) navigator.vibrate(200);
        } catch (err) {
            console.error("Order Critical Failure:", err);
            alert("Local storage error. Please restart app.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleWhatsappShare = async (order) => {
        if (!order) return;
        
        let phone = order.outletPhone || "";
        if (!phone) {
            // Fallback: Try to fetch from outletMaster if not in order doc
            try {
                const q = query(
                    collection(db, "outletMaster"), 
                    where("clientId", "==", userProfile.clientId),
                    where("outletId", "==", order.outletId)
                );
                const snap = await getDocs(q);
                if (!snap.empty) {
                    phone = snap.docs[0].data().phone;
                }
            } catch (err) {}
        }

        if (!phone) {
            alert("Mobile number not found for this outlet.");
            return;
        }

        phone = phone.replace(/\D/g, "");
        if (phone.length === 10) phone = "91" + phone;

        const itemsText = (order.itemLines || []).map(li => `*${li.skuName}* - *${li.qty}* Pcs`).join("\n");
        const distributorDisplay = order.distributorName || companyInfo.name || "VINAYAKA TRADERS";
        
        const message = `*${distributorDisplay.toUpperCase()}*\n\nShop Name: *${order.outletName}*\nInvoice: *${order.invoiceNo}*\nItems:\n${itemsText}\nAmount: *₹${order.totalAmount.toLocaleString()}*\n\nThank you for choosing us!`;
        const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
        window.open(waUrl, "_blank");
    };

    const handleEditOutlet = (outlet) => {
        setEditingOutlet(outlet);
        setNewOutletForm({ name: outlet.name, phone: outlet.phone });
        setCapturedLat(outlet.lat || null);
        setCapturedLong(outlet.long || null);
        setPhotoPreview(outlet.photoUrl || null);
        setOutletPhoto(null);
        setStep("edit_outlet");
    };

    const handleUpdateOutlet = async () => {
        if (!newOutletForm.name || !newOutletForm.phone) {
            alert("Shop Name and Mobile Number are mandatory.");
            return;
        }
        setSubmitting(true);
        try {
            setUploadStatus("Initializing upload...");
            let finalPhotoUrl = editingOutlet.photoUrl || "";
            if (outletPhoto) {
                const photoRef = ref(storage, `outlets/${editingOutlet.outletId}_${Date.now()}`);
                const uploadTask = uploadBytesResumable(photoRef, outletPhoto);
                
                finalPhotoUrl = await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setUploadProgress(Math.round(progress));
                        },
                        (error) => reject(error),
                        () => getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject)
                    );
                });
            }

            setUploadStatus("Syncing registry updates...");
            const updates = {
                name: newOutletForm.name.trim(),
                phone: newOutletForm.phone.trim(),
                distributorId: companyInfo.partyId || "",
                distributorName: companyInfo.name || "",
                photoUrl: finalPhotoUrl,
                lat: capturedLat,
                long: capturedLong,
                updatedAt: serverTimestamp()
            };

            await updateDoc(doc(db, "outletMaster", editingOutlet.id), updates);
            setUploadStatus("Update complete!"); 
            alert("Outlet updated successfully!");
            setStep("outlet");
            // Refresh list
            const qOutlets = query(
                collection(db, "outletMaster"),
                where("clientId", "==", userProfile.clientId),
                where("beat", "==", selectedBeat),
                where("status", "==", "active"),
                where("dsId", "==", userProfile.dsId || "")
            );
            const outletSnap = await getDocs(qOutlets);
            setRefreshTrigger(prev => prev + 1); // This will cause useMemo to update if we add dependencies correctly
            setBeatCache(prev => {
                const updated = { ...prev };
                delete updated[selectedBeat];
                return updated;
            });
            setStep("outlet");
        } catch (err) {
            console.error(err);
            alert("Error updating outlet: " + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCaptureNewOutletLoc = () => {
        if (!navigator.geolocation) return;
        setCapLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setCapturedLat(pos.coords.latitude.toFixed(6));
                setCapturedLong(pos.coords.longitude.toFixed(6));
                setCapLoading(false);
            },
            () => { alert("Could not capture location."); setCapLoading(false); },
            { enableHighAccuracy: true, timeout: 5000 }
        );
    };

    const handleSaveNewOutlet = async () => {
        if (!newOutletForm.name || !newOutletForm.phone || !capturedLat || !capturedLong) {
            alert("Shop Name, Mobile Number, and Lat/Long are mandatory.");
            return;
        }
        if (newOutletForm.phone.length !== 10) {
            alert("Mobile number must be exactly 10 digits.");
            return;
        }
        setSubmitting(true);
        setUploadProgress(0);
        try {
            setUploadStatus("Commencing secure upload...");
            let photoUrl = "";
            if (outletPhoto) {
                const photoRef = ref(storage, `outlets/${Date.now()}_${outletPhoto.name}`);
                const uploadTask = uploadBytesResumable(photoRef, outletPhoto);
                
                photoUrl = await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setUploadProgress(Math.round(progress));
                        },
                        (error) => reject(error),
                        () => getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject)
                    );
                });
            }
            setUploadStatus("Anchoring hub to registry...");
            const autoId = "OUT" + Date.now().toString().slice(-6);
            const payload = {
                clientId: userProfile.clientId,
                distributorId: companyInfo.partyId || "",
                distributorName: companyInfo.name || "",
                outletId: autoId,
                name: newOutletForm.name.toUpperCase(),
                phone: newOutletForm.phone,
                dsId: userProfile.dsId,
                dsName: userProfile.name,
                beat: selectedBeat,
                beatName: beatMap[selectedBeat],
                lat: Number(capturedLat),
                long: Number(capturedLong),
                photoUrl: photoUrl || null,
                status: "active",
                createdAt: serverTimestamp(),
            };
            await addDoc(collection(db, "outletMaster"), payload);
            setUploadStatus("Network synchronized successfully!");
            setRawOutlets(prev => [{ id: "temp", ...payload }, ...prev]); 
            setRefreshTrigger(prev => prev + 1);
            setBeatCache(prev => {
                const updated = { ...prev };
                delete updated[selectedBeat];
                return updated;
            });
            setStep("outlet");
            setNewOutletForm({ name: "", phone: "" });
            setCapturedLat(null);
            setCapturedLong(null);
            setPhotoPreview(null);
            alert("Outlet Added Successfully!");
        } catch (err) { console.error(err); alert("Error adding outlet."); }
        finally { 
            setSubmitting(false); 
            setUploadStatus("");
        }
    };

    if (loading) return (
        <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "#f8fafc" }}>
            <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "40px", marginBottom: "20px" }}>⚡</div>
                <div style={{ fontWeight: 800, color: "var(--primary)" }}>Syncing Field Data...</div>
            </div>
        </div>
    );

    return (
        <BlankLayout>
            <div style={container}>
                {/* GLOBAL STATUS BAR */}
                <div style={statusBar}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 10px #10b981" }}></div>
                        <span style={{ fontSize: 11, fontWeight: 800, color: "white", textTransform: "uppercase", letterSpacing: "0.05em" }}>Node: Active</span>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)" }}>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>

                {step === "beat" && !showStock && !showHistory && (
                    <div style={{ padding: "80px 24px 100px" }}>
                        <div style={{ marginBottom: 40 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Field Operations Dashboard</div>
                            <h1 style={{ fontSize: 36, fontWeight: 900, marginBottom: 8, letterSpacing: "-0.03em" }}>Hi, {userProfile?.name?.split(" ")[0] || "User"} <span style={{ fontSize: 14, color: "var(--text-muted)", fontWeight: 400 }}>| {companyInfo?.name || "Loading..."}</span></h1>
                            <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Your logistics supply chain is synced.</p>
                        </div>

                        {/* STATS STRIP */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 40 }}>
                            <div style={statCard}>
                                <div style={{ fontSize: 24, fontWeight: 900, color: "var(--primary)" }}>{todayStats.count || 0}</div>
                                <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Shops Billed</div>
                            </div>
                            <div style={statCard}>
                                <div style={{ fontSize: 24, fontWeight: 900, color: "var(--success)" }}>{todayStats.qty || 0}</div>
                                <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Units Sold</div>
                            </div>
                        </div>
                        
                        <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-main)", marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.05em" }}>OPERATIONAL AREAS</div>
                        <div style={beatGrid}>
                            {["MON", "TUE", "WED", "THU", "FRI", "SAT"].map((b) => (
                                <button key={b} onClick={() => handleBeatSelect(b)} style={beatBtn}>
                                    <div style={{ position: "absolute", top: 12, right: 12, background: "var(--primary)", color: "white", fontSize: 10, fontWeight: 900, width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 10px rgba(255, 107, 0, 0.3)" }}>
                                        {beatCounts[b] || 0}
                                    </div>
                                    <div style={{ fontSize: 32, marginBottom: 12 }}>{getBeatEmoji(b)}</div>
                                    <div style={{ fontSize: 16, fontWeight: 900, color: "var(--text-main)" }}>{b}</div>
                                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4, fontWeight: 600 }}>{beatMap[b] || "Standby"}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* BOTTOM NAVIGATION - MOBILE APP FEEL */}
                <div style={bottomNav}>
                    <div style={navItem(step === "beat")} onClick={() => { setStep("beat"); setShowHistory(false); setShowStock(false); }} className={step === "beat" ? "active" : ""}>
                        <div style={{ fontSize: 20 }}><FaStore /></div>
                        <span>Beat</span>
                    </div>
                    <div style={navItem(showStock)} onClick={() => { setShowStock(true); setShowHistory(false); setStep("beat"); }}>
                        <div style={{ fontSize: 20 }}><FaBoxes /></div>
                        <span>Stock</span>
                    </div>
                    <div style={navItem(showHistory)} onClick={() => { setShowHistory(true); setShowStock(false); setStep("beat"); }}>
                        <div style={{ fontSize: 20 }}><FaHistory /></div>
                        <span>History</span>
                    </div>
                    <div style={navItem(false)} onClick={() => auth.signOut()}>
                        <div style={{ fontSize: 20 }}><FaSignOutAlt /></div>
                        <span>Logout</span>
                    </div>
                </div>

                {showHistory && (
                    <div style={{ padding: "80px 20px 100px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
                            <h2 style={{ fontSize: 24, fontWeight: 900 }}>Order Logs</h2>
                            <button onClick={() => setShowHistory(false)} style={iconCircleBtn}>×</button>
                        </div>
                        <div style={{ background: "white", padding: 20, borderRadius: 20, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", marginBottom: 24 }}>
                            <input type="date" value={historyDate} min={minHistoryDate} max={maxHistoryDate} onChange={(e) => setHistoryDate(e.target.value)} style={{ ...fullWidthInput, marginBottom: 12 }} />
                            <button onClick={async () => {
                                try {
                                    const [y, m, d] = historyDate.split('-').map(Number);
                                    const start = new Date(y, m - 1, d, 0, 0, 0, 0);
                                    const end = new Date(y, m - 1, d, 23, 59, 59, 999);
                                    const q = query(collection(db, "Outlet_Orders"), where("dsId", "==", userProfile.dsId), where("createdAt", ">=", start));
                                    const snap = await getDocs(q);
                                    setHistoryOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => o.createdAt?.toDate() <= end));
                                } catch (err) { alert(err.message); }
                            }} style={primaryBtn}>SYNC LOGS</button>
                        </div>
                        <div style={{ display: "grid", gap: 12 }}>
                            {historyOrders.map(o => (
                                <div key={o.id} onClick={() => setSelectedHistoryOrder(o)} style={historyCard}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 800, fontSize: 15 }}>{o.outletName}</div>
                                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Inv: {o.invoiceNo} • {o.createdAt?.toDate?.().toLocaleTimeString()}</div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                        <div style={{ fontSize: 16, fontWeight: 900, color: "var(--success)" }}>₹{o.totalAmount.toLocaleString()}</div>
                                        <div style={{ fontSize: 9, fontWeight: 800, color: "var(--text-muted)" }}>{o.totalQty} Items</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {selectedHistoryOrder && (
                    <div style={modalOverlay}>
                        <div style={modalContent}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
                                <h3 style={{ margin: 0, fontWeight: 900 }}>Transaction Manifest</h3>
                                <button onClick={() => setSelectedHistoryOrder(null)} style={{ border: "none", background: "none", fontSize: 24 }}>×</button>
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Outlet</div>
                                <div style={{ fontSize: 18, fontWeight: 800 }}>{selectedHistoryOrder.outletName}</div>
                            </div>
                            <div style={{ borderTop: "1px solid #eee", padding: "16px 0" }}>
                                {selectedHistoryOrder.itemLines?.map((line, idx) => (
                                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
                                        <span style={{ color: "var(--text-main)", fontWeight: 600 }}>{line.skuName} (x{line.qty})</span>
                                        <span style={{ fontWeight: 800 }}>₹{line.totalAmount.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ background: "#f8fafc", padding: 20, borderRadius: 16, marginTop: 16 }}>
                                <div style={breakdownRow}><span>Subtotal</span> <span>₹{selectedHistoryOrder.totalBasic?.toLocaleString()}</span></div>
                                <div style={breakdownRow}><span>Tax Component</span> <span>₹{(selectedHistoryOrder.cgst + selectedHistoryOrder.sgst).toLocaleString()}</span></div>
                                <div style={{ ...breakdownRow, marginTop: 12, paddingTop: 12, borderTop: "2px dashed #e2e8f0", fontWeight: 900, fontSize: 18, color: "var(--text-main)" }}>
                                    <span>Grand Total</span>
                                    <span>₹{selectedHistoryOrder.totalAmount.toLocaleString()}</span>
                                </div>
                            </div>
                            <div style={{ marginTop: 24, display: "grid", gap: 12 }}>
                                <button onClick={() => handleWhatsappShare(selectedHistoryOrder)} style={whatsappBtn}>
                                    <FaWhatsapp style={{ fontSize: 20 }} /> WHATSAPP SHARE
                                </button>
                                <button onClick={() => generateOrderInvoicePdf(selectedHistoryOrder, skuMaster, companyInfo).save(`Inv_${selectedHistoryOrder.invoiceNo}.pdf`)} style={primaryActionBtn}>GENERATE PDF</button>
                                <button onClick={() => printToBluetoothDevice(selectedHistoryOrder, skuMaster, companyInfo)} style={{ ...primaryActionBtn, background: "#0f172a" }}>BLUETOOTH PRINT</button>
                            </div>
                        </div>
                    </div>
                )}

                {showStock && (
                    <div style={{ padding: "80px 20px 100px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
                            <h2 style={{ fontSize: 24, fontWeight: 900 }}>Stock Status</h2>
                            <button onClick={() => setShowStock(false)} style={iconCircleBtn}>×</button>
                        </div>
                        <div style={{ background: "white", padding: 20, borderRadius: 20, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", marginBottom: 24 }}>
                            <label style={labelStyle}>Inventory Date</label>
                            <input 
                                type="date" 
                                value={inventoryDate} 
                                min={minHistoryDate} 
                                max={maxHistoryDate} 
                                onChange={(e) => setInventoryDate(e.target.value)} 
                                style={{ ...fullWidthInput, marginBottom: 0 }} 
                            />
                        </div>

                        <div style={{ background: "white", borderRadius: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.03)", overflow: "hidden" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", background: "var(--surface-hover)", padding: "12px 16px", fontSize: 10, fontWeight: 900, color: "var(--text-muted)", textTransform: "uppercase" }}>
                                <div>Product</div>
                                <div style={{ textAlign: "center" }}>Open</div>
                                <div style={{ textAlign: "center" }}>Rec</div>
                                <div style={{ textAlign: "center" }}>Sale</div>
                                <div style={{ textAlign: "right" }}>Hand</div>
                            </div>
                            <div style={{ padding: "8px 0" }}>
                                {inventoryRows.map((r, i) => (
                                    <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", padding: "12px 16px", borderBottom: i === inventoryRows.length - 1 ? "none" : "1px solid #f1f5f9", alignItems: "center" }}>
                                        <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-main)" }}>{r.name}</div>
                                        <div style={{ textAlign: "center", fontSize: 13, color: "var(--text-muted)", fontWeight: 600 }}>{r.opening}</div>
                                        <div style={{ textAlign: "center", fontSize: 13, color: "var(--success)", fontWeight: 700 }}>{r.billed}</div>
                                        <div style={{ textAlign: "center", fontSize: 13, color: "var(--primary)", fontWeight: 700 }}>{r.sold}</div>
                                        <div style={{ textAlign: "right", fontSize: 15, color: "var(--text-main)", fontWeight: 900 }}>{r.closing}</div>
                                    </div>
                                ))}
                                {inventoryRows.length === 0 && (
                                    <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>No data for selected date</div>
                                )}
                            </div>
                        </div>

                        {inventoryRows.length > 0 && (
                            <button 
                                onClick={() => generateStockStatusPdf(inventoryRows, inventoryDate, userProfile?.name, companyInfo?.name).save(`Stock_${inventoryDate}.pdf`)} 
                                style={{ ...primaryBtn, marginTop: 32 }}
                            >
                                GENERATE STOCK REPORT
                            </button>
                        )}
                    </div>
                )}

                {step === "outlet" && (
                    <div style={{ padding: "80px 20px 100px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                            <h2 style={{ fontSize: 24, fontWeight: 900 }}>{selectedBeat} Matrix</h2>
                            <button onClick={() => setStep("add_outlet")} style={addCircleBtn}>+ Add</button>
                        </div>

                        <div style={{ marginBottom: 24, padding: "4px", background: "rgba(0,0,0,0.05)", borderRadius: 12, display: "flex" }}>
                            <button 
                                onClick={() => setSortByDistance(false)} 
                                style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 800, background: !sortByDistance ? "white" : "transparent" }}
                            >SEQUENCE</button>
                            <button 
                                onClick={() => { setSortByDistance(true); captureLocation(); }} 
                                style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 800, background: sortByDistance ? "white" : "transparent" }}
                            >NEAR ME</button>
                        </div>

                        <div style={{ display: "grid", gap: 16 }}>
                            {outlets.map((o) => (
                                <div key={o.id} style={outletCard}>
                                    <div style={{ position: "relative" }} onClick={() => handleOutletSelect(o)}>
                                        {o.photoUrl ? (
                                            <img src={o.photoUrl} crossOrigin="anonymous" alt="shop" style={{ width: 56, height: 56, borderRadius: 16, objectFit: "cover", border: "1px solid var(--border)" }} />
                                        ) : (
                                            <div style={{ width: 56, height: 56, background: "var(--surface-hover)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>🏪</div>
                                        )}
                                    </div>
                                    <div style={{ flex: 1 }} onClick={() => handleOutletSelect(o)}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, textTransform: "uppercase" }}>{o.name}</div>
                                            {sortByDistance && o.dist !== undefined && o.dist < 999999 && (
                                                <div style={{ fontSize: 12, fontWeight: 900, color: "var(--primary)", background: "var(--surface-hover)", padding: "2px 8px", borderRadius: 6 }}>
                                                    {o.dist.toFixed(2)} km
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>ID: {o.outletId} • {o.phone}</div>
                                        {lastOrderMap[o.outletId] && (
                                            <div style={{ 
                                                fontSize: 11, 
                                                color: lastOrderMap[o.outletId].date.toLocaleDateString() === new Date().toLocaleDateString() ? "var(--success)" : "#ef4444", 
                                                fontWeight: 700 
                                            }}>
                                                Last Bill: ₹{lastOrderMap[o.outletId].amount.toLocaleString()} ({lastOrderMap[o.outletId].date.toLocaleDateString()})
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        {o.lat && o.long && (
                                            <a 
                                                href={`https://www.google.com/maps/dir/?api=1&destination=${o.lat},${o.long}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ padding: 12, borderRadius: 12, background: "rgba(255, 107, 0, 0.05)", color: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center" }}
                                            >
                                                <FaMapMarkerAlt />
                                            </a>
                                        )}
                                        <button onClick={(e) => { e.stopPropagation(); handleEditOutlet(o); }} style={{ padding: 12, borderRadius: 12, border: "none", background: "rgba(0,0,0,0.03)", color: "var(--text-muted)" }}>
                                            <FaEdit />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {step === "add_outlet" && (
                    <>
                        {renderHeader("Add Outlet", "outlet")}
                        <div style={{ padding: "80px 20px 100px", background: "#fff", margin: 15, borderRadius: 12 }}>
                            <input style={{ ...fullWidthInput, marginBottom: 15 }} placeholder="SHOP NAME" value={newOutletForm.name} onChange={e => setNewOutletForm({ ...newOutletForm, name: e.target.value.toUpperCase() })} />
                            <input 
                                style={{ ...fullWidthInput, marginBottom: 15 }} 
                                placeholder="MOBILE NUMBER (10 DIGITS)" 
                                type="tel"
                                inputMode="numeric"
                                maxLength={10}
                                value={newOutletForm.phone} 
                                onChange={e => {
                                    const val = e.target.value.replace(/\D/g, "");
                                    if (val.length <= 10) {
                                        setNewOutletForm({ ...newOutletForm, phone: val });
                                        if (val.length === 10) handleCaptureNewOutletLoc();
                                    }
                                }} 
                            />
                            <button onClick={handleCaptureNewOutletLoc} style={{ ...primaryBtn, background: "#6c757d" }}>
                                {capLoading ? "Capturing..." : capturedLat ? `📍 CAPTURED - (${Number(capturedLat).toFixed(4)}, ${Number(capturedLong).toFixed(4)})` : "📍 CAPTURE LOCATION"}
                            </button>
                            <input type="file" accept="image/*" capture="environment" id="photo-up" style={{ display: "none" }} onChange={e => {
                                const file = e.target.files[0];
                                if (file) { setOutletPhoto(file); setPhotoPreview(URL.createObjectURL(file)); }
                            }} />
                            <label htmlFor="photo-up" style={{ ...primaryBtn, display: "block", textAlign: "center", background: "#6c757d", marginTop: 10 }}>📸 {photoPreview ? "Photo Selected" : "Shop Photo"}</label>
                            {photoPreview && <img src={photoPreview} alt="p" style={{ width: "100%", borderRadius: 8, marginTop: 10, maxHeight: 150, objectFit: "cover" }} />}
                            
                            {submitting && (
                                <div style={{ marginTop: 20, padding: 16, background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: "var(--primary)", marginBottom: 8, textTransform: "uppercase" }}>{uploadStatus}</div>
                                    <div style={{ width: "100%", height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                                        <div style={{ width: `${uploadProgress}%`, height: "100%", background: "var(--primary)", transition: "width 0.3s ease" }}></div>
                                    </div>
                                </div>
                            )}

                            <button onClick={handleSaveNewOutlet} disabled={submitting} style={{ ...primaryBtn, marginTop: 20 }}>
                                {submitting ? "Processing..." : "Save Outlet"}
                            </button>
                        </div>
                    </>
                )}

                {step === "edit_outlet" && (
                    <>
                        {renderHeader("Edit Outlet", "outlet")}
                        <div style={{ padding: "80px 20px 100px", background: "#fff", margin: 15, borderRadius: 12 }}>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>Outlet ID: <b>{editingOutlet?.outletId}</b> (Immutable)</div>
                            <input style={{ ...fullWidthInput, marginBottom: 15 }} placeholder="Shop Name" value={newOutletForm.name} onChange={e => setNewOutletForm({ ...newOutletForm, name: e.target.value })} />
                            <input 
                                style={{ ...fullWidthInput, marginBottom: 15 }} 
                                placeholder="Mobile Number" 
                                type="tel"
                                maxLength={10}
                                value={newOutletForm.phone} 
                                onChange={e => {
                                    const val = e.target.value.replace(/\D/g, "");
                                    if (val.length <= 10) setNewOutletForm({ ...newOutletForm, phone: val });
                                }} 
                            />
                            <button onClick={handleCaptureNewOutletLoc} style={{ ...primaryBtn, background: "#6c757d" }}>{capLoading ? "Updating Location..." : "📍 Update Location"}</button>
                            {capturedLat && <div style={{ fontSize: 10, color: "#28a745", marginTop: 5 }}>Location: {capturedLat}, {capturedLong}</div>}
                            
                            <input type="file" accept="image/*" capture="environment" id="photo-edit" style={{ display: "none" }} onChange={e => {
                                const file = e.target.files[0];
                                if (file) { setOutletPhoto(file); setPhotoPreview(URL.createObjectURL(file)); }
                            }} />
                            <label htmlFor="photo-edit" style={{ ...primaryBtn, display: "block", textAlign: "center", background: "#6c757d", marginTop: 15 }}>📸 {outletPhoto ? "New Photo Selected" : "Change Photo"}</label>
                            {photoPreview && <img src={photoPreview} alt="p" style={{ width: "100%", borderRadius: 8, marginTop: 10, maxHeight: 150, objectFit: "cover" }} />}
                            
                            {submitting && (
                                <div style={{ marginTop: 20, padding: 16, background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: "var(--primary)", marginBottom: 8, textTransform: "uppercase" }}>{uploadStatus}</div>
                                    <div style={{ width: "100%", height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                                        <div style={{ width: `${uploadProgress}%`, height: "100%", background: "var(--primary)", transition: "width 0.3s ease" }}></div>
                                    </div>
                                </div>
                            )}

                            <button onClick={handleUpdateOutlet} disabled={submitting} style={{ ...primaryBtn, marginTop: 20 }}>{submitting ? "Updating..." : "Update Outlet"}</button>
                        </div>
                    </>
                )}

                {step === "order" && (
                    <div style={{ padding: "80px 20px 140px" }}>
                        <div style={{ marginBottom: 24, display: "flex", gap: 16, alignItems: "center" }}>
                            <div style={{ flex: 1 }}>
                                <button onClick={() => setStep("outlet")} style={{ border: "none", background: "none", color: "var(--primary)", fontWeight: 800, display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
                                    ← BACK TO SHOPS
                                </button>
                                <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 4 }}>{selectedOutlet?.name}</h2>
                                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Inventory Provisioning Matrix</div>
                            </div>
                            {selectedOutlet?.photoUrl && (
                                <img src={selectedOutlet.photoUrl} crossOrigin="anonymous" alt="shop" style={{ width: 64, height: 64, borderRadius: 16, objectFit: "cover", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                            )}
                        </div>

                        <div style={{ display: "grid", gap: 16 }}>
                            {skuList.map((sku) => {
                                const m = skuMaster[sku];
                                const stock = stockLevels[sku] || 0;
                                const qty = orderItems[sku] || "";
                                return (
                                    <div key={sku} style={skuOrderCard}>
                                        <div style={{ width: 48, height: 48, marginRight: 4, borderRadius: 10, overflow: "hidden", background: "var(--surface-hover)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            {m?.photoUrl ? (
                                                <img src={m.photoUrl} crossOrigin="anonymous" alt="sku" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            ) : (
                                                <span style={{ fontSize: 20 }}>📦</span>
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 800, fontSize: 14 }}>{m?.Name || sku}</div>
                                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>MRP: ₹{m?.mrp} | Stock: <span style={{ color: stock > 0 ? "var(--success)" : "var(--error)", fontWeight: 800 }}>{stock}</span></div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <button onClick={() => onChangeQty(sku, Math.max(0, (orderItems[sku] || 0) - 1))} style={qtyBtnSmall}>-</button>
                                            <input 
                                                type="number" 
                                                placeholder="0"
                                                inputMode="numeric"
                                                value={qty} 
                                                onChange={(e) => onChangeQty(sku, e.target.value)}
                                                style={qtyInput}
                                            />
                                            <button onClick={() => onChangeQty(sku, (orderItems[sku] || 0) + 1)} style={{ ...qtyBtnSmall, background: "var(--primary)", color: "white" }}>+</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* STICKY FOOTER FOR ORDER */}
                        <div style={orderFooter}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" }}>Grand Total</div>
                                <div style={{ fontSize: 24, fontWeight: 900, color: "white" }}>₹{totals.totalAmount.toLocaleString()}</div>
                            </div>
                            <button 
                                onClick={() => setStep("preview")} 
                                disabled={submitting || totals.totalAmount === 0}
                                style={confirmBtn}
                            >
                                REVIEW ORDER
                            </button>
                        </div>
                    </div>
                )}

                {step === "preview" && (
                    <div style={{ padding: "80px 20px 100px" }}>
                        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 32 }}>
                            <button onClick={() => setStep("order")} style={iconCircleBtn}>←</button>
                            <h2 style={{ fontSize: 24, fontWeight: 900 }}>Check Manifest</h2>
                        </div>

                        <div style={{ background: "white", padding: 24, borderRadius: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.03)", marginBottom: 32 }}>
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Supply Target</div>
                                <div style={{ fontSize: 18, fontWeight: 800 }}>{selectedOutlet?.name}</div>
                            </div>

                            <div style={{ borderTop: "1px solid #eee", padding: "16px 0" }}>
                                {Object.entries(orderItems).map(([sku, qty]) => {
                                    if (qty <= 0) return null;
                                    const m = skuMaster[sku] || {};
                                    const basic = Number(m.Basic_Eway) || 0;
                                    const gst = Number(m.gst) || 0;
                                    const mrp = Number(m.mrp) || 0;
                                    const skutype = (m.skutype || "").toLowerCase();
                                    let lineBasic = basic;
                                    let lineTax = 0;
                                    if (skutype === "tobacco") {
                                        const unitBasic = mrp / ((100 + gst) / 100);
                                        lineTax = mrp - unitBasic;
                                    } else {
                                        lineTax = basic * (gst / 100);
                                    }
                                    const totalRate = lineBasic + lineTax;
                                    return (
                                        <div key={sku} style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 13 }}>
                                            <div style={{ flex: 1, paddingRight: 10 }}>
                                                <div style={{ fontWeight: 800 }}>{m.Name || sku}</div>
                                                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{qty} Units @ ₹{totalRate.toFixed(2)}</div>
                                            </div>
                                            <div style={{ fontWeight: 800 }}>₹{(qty * totalRate).toLocaleString()}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ background: "#f8fafc", padding: 20, borderRadius: 16, marginTop: 16 }}>
                                <div style={breakdownRow}><span>Net Weight Basic</span> <span>₹{totals.totalBasic.toLocaleString()}</span></div>
                                <div style={breakdownRow}><span>Tax Pool (GST)</span> <span>₹{(totals.cgst + totals.sgst).toLocaleString()}</span></div>
                                <div style={{ ...breakdownRow, marginTop: 12, paddingTop: 12, borderTop: "2px dashed #e2e8f0", fontWeight: 900, fontSize: 20, color: "#0f172a" }}>
                                    <span>Sync Total</span>
                                    <span>₹{totals.totalAmount.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>

                        <button onClick={handleConfirmOrder} disabled={submitting} style={confirmBtn}>
                            {submitting ? "COMMITTING TO CHAIN..." : "SYNC & GENERATE INVOICE"}
                        </button>
                    </div>
                )}

                {step === "success" && (
                    <div style={{ textAlign: "center", padding: "120px 24px" }}>
                        <div style={{ width: 80, height: 80, background: "var(--success)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 32px", boxShadow: "0 10px 30px rgba(16, 185, 129, 0.3)" }}>
                            <div style={{ fontSize: 40, color: "white" }}>✓</div>
                        </div>
                        <h2 style={{ fontSize: 32, fontWeight: 900, marginBottom: 8 }}>Order Synced</h2>
                        <p style={{ color: "var(--text-muted)", fontSize: 16, marginBottom: 40 }}>Invoice {lastOrder?.invoiceNo} is ready.</p>
                        
                        <div style={{ display: "grid", gap: 16 }}>
                            <button onClick={() => handleWhatsappShare(lastOrder)} style={whatsappBtn}>
                                <FaWhatsapp style={{ fontSize: 20 }} /> WHATSAPP SHARE
                            </button>
                            <button onClick={() => generateOrderInvoicePdf(lastOrder, skuMaster, companyInfo).save(`Inv_${lastOrder.invoiceNo}.pdf`)} style={primaryActionBtn}>
                                DOWNLOAD INVOICE
                            </button>
                            <button onClick={() => printToBluetoothDevice(lastOrder, skuMaster, companyInfo)} style={{ ...primaryActionBtn, background: "#0f172a" }}>
                                PRINT
                            </button>
                            <button onClick={() => {
                                setOrderItems({});
                                setStep("outlet");
                                if (ordersCountForAutoSort % 3 === 0) {
                                    setSortByDistance(true);
                                    captureLocation();
                                }
                            }} style={secondaryActionBtn}>NEXT OPERATION</button>
                        </div>
                    </div>
                )}

            </div>
        </BlankLayout>
    );
}

const getBeatEmoji = (b) => {
    const map = { MON: "🏗️", TUE: "🏘️", WED: "🏢", THU: "🏭", FRI: "🏬", SAT: "🗺️" };
    return map[b] || "📍";
};

// PREMIUM STYLES
const container = {
    maxWidth: "500px",
    margin: "0 auto",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif"
};

const statusBar = {
    position: "fixed",
    top: 0,
    width: "100%",
    maxWidth: "500px",
    height: "20px",
    padding: "40px 24px 30px",
    background: "linear-gradient(to bottom, #0f172a, transparent)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 1000,
    color: "white"
};

const bottomNav = {
    position: "fixed",
    bottom: 0,
    width: "100%",
    maxWidth: "500px",
    height: "84px",
    background: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(24px)",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    borderTop: "1px solid rgba(0,0,0,0.05)",
    zIndex: 1000,
    paddingBottom: "20px"
};

const navItem = (active) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: active ? "var(--primary)" : "var(--text-muted)",
    transition: "all 0.2s",
    fontSize: 22,
    cursor: "pointer"
});

const beatGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16
};

const beatBtn = {
    position: "relative",
    padding: "24px",
    borderRadius: "28px",
    border: "1px solid var(--border)",
    background: "white",
    boxShadow: "0 4px 12px rgba(0,0,0,0.01)",
    textAlign: "center",
    cursor: "pointer",
    transition: "transform 0.1s"
};

const statCard = {
    padding: "16px",
    borderRadius: "20px",
    background: "white",
    border: "1px solid var(--border)",
    textAlign: "center"
};

const iconCircleBtn = {
    width: 48,
    height: 48,
    borderRadius: "50%",
    border: "none",
    background: "white",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: 20,
    color: "var(--primary)",
    cursor: "pointer"
};

const addCircleBtn = {
    padding: "12px 24px",
    borderRadius: "12px",
    border: "none",
    background: "var(--primary)",
    color: "white",
    fontSize: 16,
    fontWeight: "900",
    textTransform: "uppercase",
    boxShadow: "0 8px 16px rgba(255, 107, 0, 0.3)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8
};

const outletCard = {
    padding: "20px",
    borderRadius: "24px",
    background: "white",
    border: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    gap: 16,
    boxShadow: "0 4px 10px rgba(0,0,0,0.01)",
    cursor: "pointer"
};

const skuOrderCard = {
    padding: "16px 20px",
    borderRadius: "16px",
    background: "white",
    border: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    gap: 16
};

const qtyBtnSmall = {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface-hover)",
    fontSize: 20,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer"
};

const qtyInput = {
    width: 70,
    textAlign: "center",
    border: "none",
    fontSize: 20,
    fontWeight: 900,
    color: "var(--text-main)",
    background: "transparent",
    outline: "none"
};

const orderFooter = {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    width: "100%",
    maxWidth: "500px",
    margin: "0 auto",
    background: "#0f172a",
    padding: "20px 24px 32px",
    display: "flex",
    alignItems: "center",
    gap: 20,
    zIndex: 1100,
    boxShadow: "0 -10px 40px rgba(0,0,0,0.4)"
};

const confirmBtn = {
    flex: 2,
    padding: "16px",
    borderRadius: 16,
    border: "none",
    background: "var(--primary)",
    color: "white",
    fontWeight: 900,
    fontSize: 16,
    letterSpacing: "0.05em",
    boxShadow: "0 10px 20px rgba(255, 107, 0, 0.4)",
    cursor: "pointer"
};

const primaryActionBtn = {
    padding: "18px",
    borderRadius: 18,
    border: "none",
    background: "var(--primary)",
    color: "white",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer"
};

const whatsappBtn = {
    padding: "18px",
    borderRadius: 18,
    border: "none",
    background: "#25D366",
    color: "white",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: "0 10px 20px rgba(37, 211, 102, 0.2)"
};

const secondaryActionBtn = {
    padding: "18px",
    borderRadius: 18,
    border: "1px solid var(--border)",
    background: "white",
    color: "var(--text-main)",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer"
};

const fullWidthInput = {
    width: "100%",
    padding: "16px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    fontSize: 16,
    background: "#f8fafc",
    outline: "none",
    boxSizing: "border-box"
};

const primaryBtn = {
    width: "100%",
    padding: "16px",
    borderRadius: 12,
    border: "none",
    background: "var(--primary)",
    color: "white",
    fontWeight: 800,
    fontSize: 14,
    boxShadow: "0 4px 10px rgba(255, 107, 0, 0.2)",
    cursor: "pointer"
};

const historyCard = {
    padding: "20px",
    borderRadius: "20px",
    background: "white",
    border: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    gap: 16,
    boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
    cursor: "pointer"
};

const formCard = {
    background: "white",
    padding: "24px",
    borderRadius: "24px",
    border: "1px solid var(--border)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.03)"
};

const labelStyle = {
    display: "block",
    fontSize: "12px",
    fontWeight: 800,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    marginBottom: "8px",
    letterSpacing: "0.05em"
};

const modalOverlay = { 
    position: "fixed", 
    top: 0, 
    left: 0, 
    right: 0, 
    bottom: 0, 
    background: "rgba(15, 23, 42, 0.8)", 
    backdropFilter: "blur(8px)", 
    zIndex: 2000, 
    padding: 20, 
    display:"flex", 
    alignItems:"center" 
};

const modalContent = { 
    background: "#fff", 
    borderRadius: 32, 
    padding: 32, 
    width: "100%", 
    maxHeight: "90%", 
    overflowY: "auto", 
    boxShadow: "0 20px 50px rgba(0,0,0,0.3)" 
};

const breakdownRow = { 
    display: "flex", 
    justifyContent: "space-between", 
    marginBottom: 8, 
    fontSize: 14,
    color: "var(--text-muted)"
};

export default DsMobileDashboard;
